import { inject } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot } from '@angular/router';
import { of } from 'rxjs';
import { CommunityService } from '../services/community/community.service';
import { CommunitySignalService } from '../services/community/community.signal.service';
import { TitleService } from '../services/meta/title.service';
import { IFileModel } from '../interfaces/file-model.interface';
import { environment as env } from '@env/environment';
import { ICommunity } from '../interfaces/community.interface';

export const CommunityMetaResolver: ResolveFn<boolean> = (route: ActivatedRouteSnapshot) => {
  const communityService = inject(CommunityService);
  const communitySignalService = inject(CommunitySignalService);
  const titleService = inject(TitleService);

  const communityId = route.paramMap.get('id') || '';

  if (!communityId) {
    return of(true);
  }

  // The community is almost always already in the signal cache from
  // LayoutComponent's initial fetch — when the user clicks the "About"
  // tab from anywhere under /community/:id, the parent layout has
  // already loaded the same community. Use the cached value and apply
  // meta tags synchronously so the resolver returns immediately and
  // the route is not held up by a duplicate network round-trip.
  const cached = communitySignalService.getCommunity();
  if (cached && cached.id === +communityId) {
    setCommunityMetaTags(cached, communityId, titleService);
    return of(true);
  }

  // Cold-load fallback: no cached community (e.g. direct deep-link to
  // /community/:id). Resolve immediately so the route is not blocked
  // on the meta-only request, and apply tags when the response lands.
  communityService.getCommunityById(+communityId).subscribe({
    next: (community) => {
      if (community) {
        setCommunityMetaTags(community, communityId, titleService);
      }
    },
    error: (error) => {
      console.error('Error in community meta resolver:', error);
    },
  });
  return of(true);
};

function setCommunityMetaTags(
  community: ICommunity,
  communityId: string,
  titleService: TitleService
): void {
  // Set page title for community
  const title = community.displayName
    ? `${community.displayName} | SeeMyLegacy`
    : 'Community | SeeMyLegacy';
  titleService.updateTitle(title);
  
  // Set description
  const desc = `Explore ${community.displayName ?? 'this community'} and discover their initiatives, campaigns, and community stories.`;
  titleService.updateDescription(desc);

  // Set OG URL for community
  const communityUrl = `${env.webUrl}/community/${communityId}`;
  titleService.updateOgUrl(communityUrl);

  // Set OG image from community profile image blob
  if (community.profileImageBlob) {
    const images = JSON.parse(community.profileImageBlob) as IFileModel[];
    if (images && images.length > 0 && images[0].BlobUrl) {
      titleService.updateOgImage(images[0].BlobUrl);
    }
  }
}
